/**
 * Playback providers.
 *
 * `BufferTrackSource` plays a decoded AudioBuffer (a locally-loaded file);
 * `StreamingTrackSource` pumps PCM fetched from af through an AudioWorklet.
 * `AudioDAW` drives both through this interface and never asks which it has.
 *
 * The interface is deliberately transport-shaped rather than node-shaped: the
 * DAW hands each source a transport position and a speed, and the source works
 * out what to play. That's what lets one shared position keep every track in
 * step — including through seeks, offset drags, and reverse.
 */

import { playbackWorkletUrl } from "./playbackWorklet.js";
import { interpolatorConfig, DEFAULT_INTERPOLATOR_ID } from "./interpolators/index.js";
import { remoteFetch } from "../core/remoteFetch.js";

export class TrackSource {
  constructor(track) {
    this.track = track;
    /** Output node the DAW connects into the track's gain chain. */
    this.output = null;
  }

  /** @returns {boolean} whether this source can produce audio right now */
  get isReady() {
    return false;
  }

  /**
   * Begin playing from a transport position.
   * @param {number} transportTime - seconds on the project timeline
   * @param {number} speed - playback rate; negative plays backwards
   */
  start() {}

  /** Stop producing audio. */
  stop() {}

  /** Move to a transport position, playing or not. */
  seek() {}

  /** Change rate without interrupting playback. Negative plays backwards. */
  setSpeed() {}

  /** Hold pitch steady while the speed changes. Not every source can. */
  setPitchPreservation() {}

  /** Speeds between which pitch is held exactly. */
  setPitchPreservationRange() {}

  /** @returns {boolean} whether this source can hold pitch at all */
  get canPreservePitch() {
    return false;
  }

  /** Ranges of the track that could be played right now, in track-local seconds. */
  getBufferedRanges() {
    return [];
  }

  destroy() {
    this.stop();
    this.output = null;
  }
}

/**
 * Local file playback: one AudioBufferSourceNode per start, as before.
 *
 * Kept because a locally-loaded file has no server to stream from, and because
 * it's the simplest thing that can possibly work for the demo path. It ignores
 * `speed` other than positive rates — reverse needs the pump.
 */
export class BufferTrackSource extends TrackSource {
  constructor(track, audioContext, destination) {
    super(track);
    this.audioContext = audioContext;
    this.output = destination;
    this.node = null;
  }

  get isReady() {
    return this.track.audioBuffer !== null;
  }

  start(transportTime, speed = 1) {
    this.stop();
    if (!this.isReady || speed <= 0) return;

    const node = this.audioContext.createBufferSource();
    node.buffer = this.track.audioBuffer;
    node.playbackRate.value = speed;
    node.connect(this.output);

    // Where this track sits relative to the transport.
    const relative = this.track.timeOffset - transportTime;
    const when = relative > 0 ? relative : 0;
    const offset = relative > 0 ? 0 : -relative;

    if (offset < this.track.duration) {
      node.start(this.audioContext.currentTime + when, offset);
      this.node = node;
    }
  }

  stop() {
    if (!this.node) return;
    try {
      this.node.stop();
    } catch {
      // already stopped
    }
    this.node.disconnect();
    this.node = null;
  }

  seek(transportTime, speed, isPlaying) {
    if (isPlaying) this.start(transportTime, speed);
    else this.stop();
  }

  setSpeed(speed) {
    if (this.node && speed > 0) this.node.playbackRate.value = speed;
  }

  getBufferedRanges() {
    return this.isReady ? [{ start: 0, end: this.track.duration }] : [];
  }
}

/**
 * Streamed playback through the sample pump.
 *
 * Fetches fixed, aligned PCM blocks around the read position and feeds them to
 * the worklet. Prefetch follows the sign of the speed, so reverse walks block
 * indices downward and the server sees ordinary aligned reads either way.
 */
export class StreamingTrackSource extends TrackSource {
  #manifest;
  #baseUrl;
  #blockSamples;
  #context;
  #node = null;
  #inFlight = new Set();
  /** block -> when it was posted, so a post that never lands is retried. */
  #posted = new Map();
  #destroyed = false;

  /** Source-sample position last reported by the worklet. */
  #position = 0;
  #speed = 0;
  #playing = false;

  constructor(track, manifest, manifestUrl, audioContext, destination, options = {}) {
    super(track);

    this.#manifest = manifest;
    this.#context = audioContext;
    this.output = destination;
    this.#blockSamples = manifest.pcm.blockSamples;

    const documentBase = typeof document !== "undefined" ? document.baseURI : undefined;
    this.#baseUrl = new URL(manifestUrl, documentBase).toString();

    this.bits = options.bits ?? 16;
    /** How far ahead (and behind) to keep fed, in seconds. */
    this.lookAhead = options.lookAhead ?? 4;
    this.onPosition = options.onPosition || null;

    this.stats = { requests: 0, bytes: 0, underruns: 0 };
    this.stalled = false;
    this.buffered = { start: 0, end: 0 };
    this.interpolatorId = options.interpolator ?? DEFAULT_INTERPOLATOR_ID;
    this.preservePitch = options.preservePitch ?? false;
    this.pitchRange = options.pitchRange ?? { min: 0.5, max: 2 };
    this.fetchOptions = options.fetchOptions ?? null;
  }

  get canPreservePitch() {
    return true;
  }

  get isReady() {
    return this.#node !== null;
  }

  /**
   * Load the worklet and build the node. Separate from `start()` because
   * `addModule` is async and the transport must not be.
   */
  async prepare() {
    if (this.#node || this.#destroyed) return;

    await this.#context.audioWorklet.addModule(playbackWorkletUrl());
    if (this.#destroyed) return;

    // Ring capacity: enough for the look-ahead window in both directions, with
    // room to spare, rounded to whole blocks.
    const wanted = this.#blockSamples * 8;

    this.#node = new AudioWorkletNode(this.#context, "daw-playback", {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      processorOptions: {
        capacity: wanted,
        rateRatio: this.#manifest.sampleRate / this.#context.sampleRate,
        sampleCount: this.#manifest.sampleCount,
        position: 0,
      },
    });

    this.#node.port.onmessage = (event) => this.#handleMessage(event.data);
    this.setInterpolator(this.interpolatorId);
    this.setPitchPreservation(this.preservePitch);
    this.setPitchPreservationRange(this.pitchRange.min, this.pitchRange.max);
    this.#node.connect(this.output);
  }

  /**
   * Hold pitch steady while the speed changes (WSOLA).
   *
   * A message like the interpolator switch, and for the same reason — the pump
   * already has the code, so this lands within a render quantum and can be A/B'd
   * by ear mid-sentence. It does nothing at 1x, where there's no ratio to
   * stretch by, and is bypassed in reverse.
   *
   * @param {boolean} enabled
   */
  setPitchPreservation(enabled) {
    this.preservePitch = !!enabled;
    this.#node?.port.postMessage({ type: "pitch", value: this.preservePitch });
  }

  /**
   * Speeds between which pitch is held exactly.
   *
   * Outside them the stretch factor clamps, so pitch drifts by the excess rather
   * than the stretcher warbling. The clamp is continuous, so a jog crossing the
   * boundary doesn't click.
   *
   * @param {number} min
   * @param {number} max
   */
  setPitchPreservationRange(min, max) {
    this.pitchRange = { min, max };
    this.#node?.port.postMessage({ type: "pitchRange", min, max });
  }

  /**
   * Swap the interpolation kernel.
   *
   * Every kernel is already compiled into the worklet, so this is a message
   * rather than a reload — the switch lands within a render quantum and
   * playback doesn't even blink, which is what makes A/B by ear possible.
   *
   * @param {string} id - from `interpolatorCatalogue()`
   */
  setInterpolator(id) {
    // The rate ratio is what the hybrid's filter is designed from.
    const config = interpolatorConfig(id, {
      fileRate: this.#manifest.sampleRate,
      contextRate: this.#context.sampleRate,
    });
    this.interpolatorId = config.id;
    if (!this.#node) return;

    this.#node.port.postMessage({
      type: "interpolator",
      kernel: config.kernel,
      table: config.table,
      params: config.params,
      before: config.before,
      after: config.after,
      identityAtZero: config.identityAtZero,
    });
  }

  #handleMessage(message) {
    if (message.type !== "position") return;

    this.#position = message.position;
    this.stalled = message.stalled;
    this.stats.underruns = message.underruns;
    this.buffered = { start: message.bufferedStart, end: message.bufferedEnd };

    this.onPosition?.(this.#position / this.#manifest.sampleRate);
    this.#fill();
  }

  #url(start, count) {
    const url = new URL("pcm", this.#baseUrl);
    url.searchParams.set("start", String(start));
    url.searchParams.set("count", String(count));
    url.searchParams.set("bits", String(this.bits));
    return url.toString();
  }

  /**
   * Whether the pump already holds a block.
   *
   * The authority is the ring the worklet reports, not a set maintained here.
   * A private "we sent it" set drifts out of true in both directions — it goes
   * stale when the ring evicts, and it discards knowledge the pump still has
   * when a seek lands inside buffered audio.
   */
  #isBuffered(block) {
    const start = block * this.#blockSamples;
    return start >= this.buffered.start && start + this.#blockSamples <= this.buffered.end;
  }

  /**
   * A block posted very recently may not be in a report yet. Hold it briefly so
   * it isn't requested twice, but let it expire so a post the pump rejected
   * (disjoint from the ring, say) is retried rather than lost.
   */
  #recentlyPosted(block) {
    const at = this.#posted.get(block);
    if (at === undefined) return false;
    if (performance.now() - at > 500) {
      this.#posted.delete(block);
      return false;
    }
    return true;
  }

  /**
   * Fetch whatever the look-ahead window needs.
   *
   * Blocks are fixed and aligned, so a jog that crosses the same ground
   * repeatedly re-requests nothing — the server and the HTTP cache both see the
   * same URLs.
   */
  #fill() {
    if (this.#destroyed || !this.#node) return;

    const rate = this.#manifest.sampleRate;
    const reach = this.lookAhead * rate;

    // Ahead in the direction of travel, with a smaller margin behind so a
    // direction flip doesn't stall instantly.
    const direction = this.#speed < 0 ? -1 : 1;
    const from = this.#position + (direction > 0 ? -reach * 0.25 : -reach);
    const to = this.#position + (direction > 0 ? reach : reach * 0.25);

    const firstBlock = Math.max(0, Math.floor(from / this.#blockSamples));
    const lastBlock = Math.min(
      Math.ceil(this.#manifest.sampleCount / this.#blockSamples) - 1,
      Math.floor(to / this.#blockSamples)
    );

    const wanted = [];
    for (let block = firstBlock; block <= lastBlock; block++) {
      if (this.#isBuffered(block) || this.#inFlight.has(block) || this.#recentlyPosted(block)) {
        continue;
      }
      wanted.push(block);
    }

    // Nearest first: the block under the cursor matters more than the edges.
    const cursorBlock = Math.floor(this.#position / this.#blockSamples);
    wanted.sort((a, b) => Math.abs(a - cursorBlock) - Math.abs(b - cursorBlock));

    for (const block of wanted.slice(0, 4)) this.#fetchBlock(block);
  }

  async #fetchBlock(block) {
    this.#inFlight.add(block);
    const start = block * this.#blockSamples;

    try {
      const response = await remoteFetch(this.#url(start, this.#blockSamples), this.fetchOptions);
      if (!response.ok) throw new Error(`pcm ${start} failed: ${response.status}`);

      const buffer = await response.arrayBuffer();
      if (this.#destroyed || !this.#node) return;

      this.stats.requests++;
      this.stats.bytes += buffer.byteLength;

      // 16-bit is the default on the wire; the pump works in float.
      let data;
      if (this.bits === 32) {
        data = new Float32Array(buffer);
      } else {
        const ints = new Int16Array(buffer);
        data = new Float32Array(ints.length);
        for (let i = 0; i < ints.length; i++) data[i] = ints[i] / 32767;
      }

      this.#posted.set(block, performance.now());
      this.#node.port.postMessage({ type: "samples", start, data }, [data.buffer]);
    } catch {
      // Leave it unsent; the next fill will retry.
    } finally {
      this.#inFlight.delete(block);
    }
  }

  /** Track-local sample position for a transport time. */
  #toSourceSample(transportTime) {
    return Math.max(0, (transportTime - this.track.timeOffset) * this.#manifest.sampleRate);
  }

  start(transportTime, speed = 1) {
    if (!this.#node) return;
    this.#playing = true;

    const position = this.#toSourceSample(transportTime);

    // Only reset if we're actually somewhere else. Going through a full seek
    // here would wipe the ring that `prebuffer()` just filled, and playback
    // would open with an underrun for no reason.
    if (Math.abs(position - this.#position) > 1) {
      this.seek(transportTime, speed, true);
      return;
    }

    this.#speed = speed;
    this.#node.port.postMessage({ type: "speed", value: speed });
    this.#fill();
  }

  stop() {
    this.#playing = false;
    this.#speed = 0;
    this.#node?.port.postMessage({ type: "stop" });
  }

  seek(transportTime, speed = 1, isPlaying = this.#playing) {
    if (!this.#node) return;

    const position = this.#toSourceSample(transportTime);

    // Nothing is discarded here. The pump keeps its ring when the target is
    // already inside it, and `#isBuffered` reads the ring back, so seeking
    // around inside loaded audio costs no requests at all.
    this.#position = position;
    this.#node.port.postMessage({ type: "seek", position });

    this.#playing = isPlaying;
    this.#speed = isPlaying ? speed : 0;
    this.#node.port.postMessage({ type: "speed", value: this.#speed });

    this.#fill();
  }

  setSpeed(speed) {
    if (!this.#node) return;
    this.#speed = this.#playing ? speed : 0;
    this.#node.port.postMessage({ type: "speed", value: this.#speed });
    this.#fill();
  }

  getBufferedRanges() {
    const rate = this.#manifest.sampleRate;
    if (this.buffered.end <= this.buffered.start) return [];
    return [{ start: this.buffered.start / rate, end: this.buffered.end / rate }];
  }

  /**
   * Wait until the block under the playhead has reached the pump.
   *
   * Without this, `play()` starts into an empty ring and the first fraction of a
   * second is silence plus an underrun. Resolves as soon as the covering block
   * is sent, or after `timeout` so a slow server delays rather than blocks.
   */
  async prebuffer(transportTime, timeout = 3000) {
    if (!this.#node) return;

    this.#position = this.#toSourceSample(transportTime);
    const needed = Math.floor(this.#position / this.#blockSamples);
    this.#fill();

    const deadline = Date.now() + timeout;
    while (!this.#isBuffered(needed) && Date.now() < deadline && !this.#destroyed) {
      await new Promise((resolve) => setTimeout(resolve, 20));
      this.#fill();
    }
  }

  /**
   * Test hook: play `length` output samples from an exact source position at an
   * exact speed, and hand back what the pump produced.
   */
  captureOutput(length, { position, speed = 1 } = {}) {
    return new Promise((resolve, reject) => {
      if (!this.#node) return reject(new Error("source not prepared"));

      const previous = this.#node.port.onmessage;
      this.#node.port.onmessage = (event) => {
        if (event.data.type === "capture") {
          this.#node.port.onmessage = previous;
          resolve(event.data.data);
        } else {
          previous?.(event);
        }
      };
      this.#node.port.postMessage({ type: "capture", length, position, speed });
    });
  }

  destroy() {
    this.#destroyed = true;
    this.stop();
    if (this.#node) {
      this.#node.port.onmessage = null;
      this.#node.disconnect();
      this.#node = null;
    }
    this.output = null;
  }
}
